import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

interface ApiCall {
  queryKey: string;
  queryFn: (params?: any) => Promise<any>;
}

// Replacement for useFetchApiGet
export function useFetchApiGet<TData = any>(
  apiCall: ApiCall,
  options?: {
    resourceName?: string;
    enabled?: boolean;
  }
) {
  const { resourceName = 'item' } = options || {};
  const [params, setParams] = useState<any>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const queryResult = useQuery({
    queryKey: [apiCall.queryKey, params],
    queryFn: async () => {
      const response = await apiCall.queryFn(params);
      return response[resourceName] ?? response;
    },
    enabled: hasLoaded,
  });

  const load = useCallback((newParams?: any) => {
    setParams(newParams);
    setHasLoaded(true);
  }, []);

  const refresh = useCallback(() => {
    queryResult.refetch();
  }, [queryResult]);

  return {
    data: queryResult.data ?? null,
    load,
    isLoading: queryResult.isLoading || queryResult.isFetching,
    error: queryResult.error,
    refetch: queryResult.refetch,
    refresh,
  };
}

// Replacement for useFetchApiList
export function useFetchApiList<TData = any[]>(
  apiCall: ApiCall,
  options?: {
    resourceName?: string;
  }
) {
  const { resourceName = 'items' } = options || {};
  const [params, setParams] = useState<any>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [paging, setPaging] = useState<any>(null);

  const queryResult = useQuery({
    queryKey: [apiCall.queryKey, params],
    queryFn: async () => {
      const response = await apiCall.queryFn(params);
      if (response.paging) {
        setPaging(response.paging);
      }
      // A zero-result page can come back as `{ data: null, paging: {...} }`
      // (explicit null, not a missing key) — `??` treats that as present and
      // falls through to `response` itself instead of an empty list, so
      // guard explicitly rather than relying on the fallback chain alone.
      const list = response[resourceName] ?? response.data ?? response ?? [];
      return Array.isArray(list) ? list : [];
    },
    enabled: hasLoaded,
  });

  const load = useCallback((newParams?: any) => {
    setParams(newParams);
    setHasLoaded(true);
  }, []);

  const loadMore = useCallback((additionalParams?: any) => {
    setParams((prev: any) => ({ ...prev, ...additionalParams }));
    setHasLoaded(true);
  }, []);

  const refresh = useCallback(() => {
    queryResult.refetch();
  }, [queryResult]);

  return {
    data: queryResult.data ?? [],
    load,
    loadMore,
    isLoading: queryResult.isLoading,
    isLoadingMore: queryResult.isFetching && !queryResult.isLoading,
    paging,
    error: queryResult.error,
    refetch: queryResult.refetch,
    refresh,
  };
}
